import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Card, EmptyState, PageHeader } from "../components/ui";

const ROLE_LABEL: Record<string, string> = {
  learner: "Learner",
  applicant: "Applicant",
  apprentice: "Apprentice",
  mentor: "Mentor",
  instructor: "Instructor",
  "program-director": "Program director",
  "compliance-admin": "Compliance admin",
  "tenant-admin": "Organization admin",
  employer: "Employer",
  auditor: "Auditor",
  "platform-operator": "Platform operator",
};
const SCOPE_LABEL: Record<string, string> = { tenant: "for the whole organization", program: "for a program", employer_participation: "for an employer's participation", platform: "across the platform" };

/** The empty shell; the apprentice's Home (ACC-04) replaces it with Slice A. Signed in, it says who and where, and what roles they hold. */
export function HomePage() {
  const { status, me, config } = useAuth();
  const productName = me?.tenant.productName ?? config.productName;
  if (status === "signed-in" && me) {
    return (
      <>
        <PageHeader title="Home" subtitle={`Signed in to ${productName} as ${me.person.displayName}.`} />
        <Card title="Your account">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
            <dt className="text-muted">Name</dt>
            <dd data-testid="me-name">{me.person.displayName}</dd>
            <dt className="text-muted">Email</dt>
            <dd>{me.person.email}</dd>
            <dt className="text-muted">Organization</dt>
            <dd>{me.tenant.displayName}</dd>
            {me.identityProvider && (
              <>
                <dt className="text-muted">Signs in through</dt>
                <dd>{me.identityProvider}</dd>
              </>
            )}
          </dl>
          <p className="mt-4">
            <Link to="/account/security">Security: passkeys, authenticator, password, activity</Link>
          </p>
        </Card>
        <div className="mt-6">
          <Card title="Your roles">
            {/* profile-settings Req 30: what I can do, from the server's view of the matrix — never from the token. */}
            <ul className="grid gap-2" data-testid="my-roles">
              {me.roles.map((role) => {
                const grant = me.grants.find((g) => g.role === role);
                return (
                  <li key={role} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-semibold">{ROLE_LABEL[role] ?? role}</span>
                    <span className="text-muted">{role === "learner" ? "everyone in the organization" : grant ? SCOPE_LABEL[grant.scopeKind] : ""}</span>
                    {grant?.expiresAt && <span className="text-muted">until {grant.expiresAt.slice(0, 10)}</span>}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-muted">{me.permissions.length} things you can do here come from these roles. What you cannot do is not shown to you as a control.</p>
          </Card>
        </div>
        <div className="mt-6">
          <EmptyState title="Nothing to do yet" hint="The first apprentice flows — applying, signing an agreement, logging hours — arrive with Slice A." />
        </div>
      </>
    );
  }
  return (
    <>
      <PageHeader title="Home" subtitle={`${productName} is being built.`} />
      <EmptyState title="Nothing here yet" hint="Sign in to see your account." action={<Link to="/login">Sign in</Link>} />
    </>
  );
}
